import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { useDeleteVoiceSession, useDiscordConsent, useMyDiscord, useTeamDiscord, useUnlinkDiscord, useUnlinkDiscordTeam, useVoiceSessions } from "../lib/discord-queries.ts";
import { errorText } from "../lib/errors.ts";
import { Avatar } from "./Avatar.tsx";
import { Icon } from "./Icon.tsx";

const when = (iso: string, lang: string) => new Date(iso).toLocaleString(lang, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
const length = (from: string, to: string | null) => (to ? Math.max(1, Math.round((Date.parse(to) - Date.parse(from)) / 60_000)) : null);

/**
 * The Team page's Discord card: the server the bot records for, each member's link
 * and consent (mine to change), and the recordings kept.
 */
export function TeamDiscordCard({ teamId, meId }: { teamId: string; meId: string | undefined }) {
  const { t, i18n } = useTranslation();
  const discord = useTeamDiscord(teamId);
  const mine = useMyDiscord();
  const consent = useDiscordConsent();
  const unlink = useUnlinkDiscord();
  const unlinkTeam = useUnlinkDiscordTeam(teamId);
  const sessions = useVoiceSessions(teamId);
  const remove = useDeleteVoiceSession(teamId);
  const data = discord.data;
  const me = mine.data;
  // Links from the bot and the link pages open here.
  const location = useLocation();
  const card = useRef<HTMLElement>(null);
  useEffect(() => {
    if (location.hash === "#discord" && data) card.current?.scrollIntoView({ block: "start" });
  }, [location.hash, data]);

  return (
    <section ref={card} className="card discord-team" id="discord" aria-labelledby="discord-title">
      <h2 id="discord-title" className="eyebrow">
        {t("discord.title")}
      </h2>
      {discord.isPending ? (
        <p className="muted small">{t("common.loading")}</p>
      ) : discord.error || !data ? (
        <p className="field-error small">{errorText(t, discord.error)}</p>
      ) : !data.botReady ? (
        <p className="muted small">{t("discord.off")}</p>
      ) : (
        <>
          {data.guild ? (
            <p className="small">
              {t("discord.server", { name: data.guild.name })}
              {data.canManage && (
                <button
                  type="button"
                  className="link-button discord-unlink"
                  disabled={unlinkTeam.isPending}
                  onClick={() => window.confirm(t("discord.unlinkServerConfirm", { name: data.guild!.name })) && unlinkTeam.mutate()}
                >
                  {t("discord.unlinkServer")}
                </button>
              )}
            </p>
          ) : (
            <ol className="discord-steps small">
              <li>
                {data.inviteUrl ? (
                  <a href={data.inviteUrl} target="_blank" rel="noreferrer">
                    {t("discord.stepInvite")} <Icon name="external" size={12} />
                  </a>
                ) : (
                  t("discord.stepInviteNoLink")
                )}
              </li>
              <li>{t("discord.stepTeam")}</li>
              <li>{t("discord.stepLink")}</li>
              <li>{t("discord.stepRecord")}</li>
            </ol>
          )}

          <div className="discord-me">
            {me ? (
              <>
                <label className="switch-row">
                  <span>
                    <b>{t("discord.myConsent", { name: me.discordName })}</b>
                    <span className="muted small">{t("discord.consentHelp")}</span>
                  </span>
                  <input type="checkbox" role="switch" className="switch" checked={me.consent} disabled={consent.isPending} onChange={(e) => consent.mutate(e.target.checked)} />
                </label>
                <button type="button" className="link-button small" disabled={unlink.isPending} onClick={() => window.confirm(t("discord.unlinkConfirm")) && unlink.mutate()}>
                  {t("discord.unlink")}
                </button>
              </>
            ) : (
              <p className="muted small">{t("discord.notLinked")}</p>
            )}
          </div>

          <ul className="discord-members" aria-label={t("discord.members")}>
            {data.members.map((m) => (
              <li key={m.userId}>
                <Avatar name={m.name} url={m.avatarUrl} me={m.userId === meId} />
                <span className="discord-member-name">{m.name}</span>
                <span className={`discord-state ${m.discordName ? (m.consent ? "is-on" : "is-off") : "is-none"}`}>
                  {m.discordName ? (m.consent ? t("discord.recorded") : t("discord.notRecorded")) : t("discord.noAccount")}
                </span>
              </li>
            ))}
          </ul>

          {!!sessions.data?.length && (
            <>
              <h3 className="eyebrow discord-sub">{t("discord.recordings")}</h3>
              <ul className="discord-sessions">
                {sessions.data.slice(0, 8).map((s) => (
                  <li key={s.id}>
                    <span>
                      <b>{s.channelName}</b>
                      <span className="muted small">
                        {when(s.startedAt, i18n.language)}
                        {length(s.startedAt, s.endedAt) && ` · ${t("discord.minutes", { n: length(s.startedAt, s.endedAt) })}`}
                        {` · ${s.tracks.map((x) => x.name).join(", ")}`}
                      </span>
                    </span>
                    {s.canDelete && (
                      <button
                        type="button"
                        className="btn btn-quiet btn-icon btn-small"
                        aria-label={t("discord.deleteRecording", { name: s.channelName })}
                        title={t("discord.deleteRecording", { name: s.channelName })}
                        disabled={remove.isPending}
                        onClick={() => window.confirm(t("discord.deleteRecordingConfirm")) && remove.mutate(s.id)}
                      >
                        <Icon name="trash" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
              <p className="muted small">{t("discord.addHelp")}</p>
            </>
          )}
        </>
      )}
    </section>
  );
}
